import React, { useState } from 'react';
import PropTypes, { oneOfType } from 'prop-types';
import styled, { css } from 'styled-components';
import { connect, useSelector } from 'react-redux';
import { matchPath } from 'react-router';
import Menu, { setActiveItems } from '../../components/Menu';
import MenuItem from '../../components/MenuItem';
import { ENABLE_ROLES_PERMISSIONS, ENABLE_SIDENAV_MENU, ORG_PATHS } from '../../constants';
import { injectParams } from '../../utils/pathHelpers';
import { getPurchaseAccess, getSelectedProfileBillingAccounts, getViewTransactionHistoryAccess } from '../../selectors/OrgManagementSelectors';
import DownChevron from '../../../../img/sprite/down-chevron.svg';

const SharedIconStyles = css`
  text-decoration: none;
  display: inline-block;
  font-size: 13px;
  li li & {
    display: none;
  }
`;

const ActiveIcon = styled.svg`
  ${SharedIconStyles}
  margin-left: 0.692em;
  margin-top: -0.308em;
`;

const InactiveIcon = styled.svg`
  ${SharedIconStyles}
  margin-left: 0.462em;
  margin-top: -0.231em;
  transform: rotate(-90deg);
`;

const NavHeader = styled.h1`
  font-size: 11px;
  font-weight: 600;
  color: #999999;
  text-transform: uppercase;
`;

const renderOrderHistoryItems = ({
  organizationId,
  billingAccounts,
  NavItem,
  NavList
}) => {
  if (billingAccounts.length === 0) {
    return (
      <NavItem
        title="Order History"
        href={injectParams(ORG_PATHS.ORDER_HISTORY_BASE, { organizationId })}
        data-qa-hook="orderHistory.link"
        isChild={ENABLE_SIDENAV_MENU ? true : false}
      />
    );
  } else if (billingAccounts.length === 1) {
    const account = billingAccounts[0];
    return (
      <NavItem
        title="Order History"
        href={injectParams(ORG_PATHS.ORDER_HISTORY, {
          organizationId,
          billingAccountId: account.id
        })}
        data-qa-hook="orderHistory.link"
        isChild={ENABLE_SIDENAV_MENU ? true : false}
      />
    );
  } else {
    const firstAccount = billingAccounts[0];
    return (
      <NavItem
        title="Order History"
        href={injectParams(ORG_PATHS.ORDER_HISTORY, {
          organizationId,
          billingAccountId: firstAccount.id
        })}
        data-qa-hook="orderHistory.link"
        isChild={ENABLE_SIDENAV_MENU ? true : false}
      >
        <NavList dropDown={ENABLE_SIDENAV_MENU ? true : false}>
          {billingAccounts.map(account => (
            <NavItem
              key={account.id}
              title={`Acct ${account.accountNumber}`}
              href={injectParams(ORG_PATHS.ORDER_HISTORY, {
                organizationId,
                billingAccountId: account.id
              })}
              data-qa-hook={`orderHistory.link.${account.id}`}
              isChild={ENABLE_SIDENAV_MENU ? true : false}
            />
          ))}
        </NavList>
      </NavItem>
    );
  }
};

/* eslint-disable react/prop-types */
const renderBillingAccountItems = ({
  organizationId,
  billingAccounts,
  NavItem,
  NavList,
  hasPurchaseOnBehalfOf,
  isMobile,
  location
}) => {
  if (billingAccounts.length === 0 && hasPurchaseOnBehalfOf) {
    return (
      <NavItem
        title="Billing Activity"
        href={injectParams(ORG_PATHS.BILLING_ACTIVITY_BASE, { organizationId })}
        data-qa-hook="accountActivity.link"
        isChild={ENABLE_SIDENAV_MENU ? true : false}
      />
    );
  } else if (billingAccounts.length === 1 && hasPurchaseOnBehalfOf) {
    const account = billingAccounts[0];
    const billingActivityPath = injectParams(ORG_PATHS.BILLING_ACTIVITY, {
        organizationId,
        billingAccountId: account.id
      });
    return (
      <NavItem
        title='Billing Activity'
        href={billingActivityPath}
        data-qa-hook="accountActivity.link"
        isChild={ENABLE_SIDENAV_MENU ? true : false}
      />
    );
  } else if(hasPurchaseOnBehalfOf){
    const firstAccount = billingAccounts[0];
    const firstBillingActivityPath = injectParams(ORG_PATHS.BILLING_ACTIVITY, {
        organizationId,
        billingAccountId: firstAccount.id
      });
    return (
      <NavItem
        title='Billing Activity'
        href={firstBillingActivityPath}
        data-qa-hook="accountActivity.link"
        isChild={ENABLE_SIDENAV_MENU ? true : false}
      >
        <NavList dropDown={ENABLE_SIDENAV_MENU ? true : false}>
          {billingAccounts.map(account => (
            <NavItem
              key={account.id}
              title={`Acct ${account.accountNumber}`}
              href={injectParams(ORG_PATHS.BILLING_ACTIVITY, {
                organizationId,
                billingAccountId: account.id
              })}
              data-qa-hook={`accountActivity.link.${account.id}`}
              isChild={ENABLE_SIDENAV_MENU ? true : false}
            />
          ))}
        </NavList>
      </NavItem>
    );
  }
};

const renderAccount = ({
  organizationId,
  NavItem,
  NavList,
  isMobile,
  showAccountSubItems,
  setShowAccountSubItems
}) => {

  if (isMobile) {
    return (
      <NavItem
        firstChild
        title="Org Account"
        data-qa-hook="orgAccount.link"
        btnClick={() => setShowAccountSubItems(showing => !showing)}
        showChildren={showAccountSubItems}
      >
        <NavList showChildren={showAccountSubItems}>
          <NavItem
            title="Profile"
            isChild={true}
            data-qa-hook="profile.link"
            href={injectParams(ORG_PATHS.PROFILE, { organizationId })}
          />
          <NavItem
            title="Addresses"
            isChild={true}
            data-qa-hook="profile.link"
            href={injectParams(ORG_PATHS.ADDRESS_MANAGEMENT, {
              organizationId
            })}
          />
            <NavItem
              title="Subscriptions"
              isChild={true}
              data-qa-hook="profile.link"
              href={injectParams(ORG_PATHS.SUBSCRIPTIONS, { organizationId })}
            />
        </NavList>
      </NavItem>
    );
  }

  return (
    <NavItem
      firstChild
      title="Org Account"
      data-qa-hook="orgAccount.link"
      href={injectParams(ORG_PATHS.PROFILE, { organizationId })}
    >
      <NavList>
        <NavItem
          title="Profile"
          isChild={true}
          data-qa-hook="profile.link"
          href={injectParams(ORG_PATHS.PROFILE, { organizationId })}
        />
        <NavItem
          title="Addresses"
          isChild={true}
          data-qa-hook="profile.link"
          href={injectParams(ORG_PATHS.ADDRESS_MANAGEMENT, { organizationId })}
        />
        <NavItem
          title="Subscriptions"
          isChild={true}
          data-qa-hook="profile.link"
          href={injectParams(ORG_PATHS.SUBSCRIPTIONS, { organizationId })}
        />
      </NavList>
    </NavItem>
  );
};

/* eslint-enable react/prop-types */

export const OrgNavMenu = props => {
  const {
    location,
    isOpen,
    navToggle,
    organizationId,
    billingAccounts,
    hasPurchaseOnBehalfOf,
    isMobile,
  } = props;
  const [showAccountSubItems, setShowAccountSubItems] = useState(false);

  const handleViewTransactionHistoryAccess = useSelector(state => getViewTransactionHistoryAccess(state, organizationId, billingAccounts.map(account => account.accountNumber)));
  const hasViewTransactionHistoryAccess = ENABLE_ROLES_PERMISSIONS ? handleViewTransactionHistoryAccess.some(permission => permission.hasAccess === true) : true;

  const styles = props.styles || {};
  const icons = props.icons || {
    activeIcon: (
      <ActiveIcon>
        <use xlinkHref={`#${DownChevron.id}`} />
      </ActiveIcon>
    ),
    inactiveIcon: (
      <InactiveIcon>
        <use xlinkHref={`#${DownChevron.id}`} />
      </InactiveIcon>
    )
  };
  const NavItem = props => (
    <MenuItem
      location={location}
      icons={icons}
      styles={styles}
      navToggle={navToggle}
      isOpen={isOpen}
      {...props}
    />
  );
  const NavList = props => <Menu styles={styles} {...props} />;

  const AccountItems = renderAccount({
    organizationId,
    NavItem,
    NavList,
    isMobile,
    showAccountSubItems,
    setShowAccountSubItems
  });

  const OrderHistoryItems = renderOrderHistoryItems({
    organizationId,
    billingAccounts,
    NavItem,
    NavList
  });
  const AccountActivityItems = renderBillingAccountItems({
    organizationId,
    billingAccounts,
    NavItem,
    NavList,
    isMobile,
    hasPurchaseOnBehalfOf,
    location,
  });

  const menu = (
    <NavList isActive>
      {AccountItems}
      {OrderHistoryItems}
      <NavItem
        title="Autoship"
        href={injectParams(ORG_PATHS.AUTOSHIP, { organizationId })}
        data-qa-hook="autoship.link"
      />
      {AccountActivityItems}
      <NavItem
        lastChild={!isMobile}
        title="Groups"
        href={injectParams(ORG_PATHS.GROUPS, { organizationId })}
        data-qa-hook="groups.link"
      />
    </NavList>
  )

  const updatedMenu = (
    <NavList isActive>
      <NavHeader>Orders</NavHeader>
      {OrderHistoryItems}
      <NavItem
        isChild={true}
        title="Autoship"
        href={injectParams(ORG_PATHS.AUTOSHIP, { organizationId })}
        data-qa-hook="autoship.link"
      />
      <NavItem
        title="Subscriptions"
        isChild={true}
        data-qa-hook="profile.link"
        href={injectParams(ORG_PATHS.SUBSCRIPTIONS, { organizationId })}
      />
      {hasViewTransactionHistoryAccess && AccountActivityItems}
      <NavHeader>Settings</NavHeader>
      <NavItem
        title="Organization Profile"
        isChild={true}
        data-qa-hook="profile.link"
        href={injectParams(ORG_PATHS.PROFILE, { organizationId })}
      />
      <NavItem
        title="Addresses"
        isChild={true}
        data-qa-hook="profile.link"
        href={injectParams(ORG_PATHS.ADDRESS_MANAGEMENT, { organizationId })}
      />
      <NavItem
        isChild
        lastChild={!isMobile}
        title="Groups"
        href={injectParams(ORG_PATHS.GROUPS, { organizationId })}
        data-qa-hook="groups.link"
      />
    </NavList>
  );

  const _crumbs = [];
  const navMenu = setActiveItems(ENABLE_SIDENAV_MENU ? updatedMenu : menu, props, 0, false, _crumbs);

  return navMenu;
};

const { object, func, bool, shape, node, string, array, arrayOf } = PropTypes;

OrgNavMenu.propTypes = {
  billingAccounts: arrayOf(
    shape({
      billingAccountId: string,
      organizationName: string
    })
  ).isRequired,

  location: object.isRequired,
  navToggle: func,
  noRender: bool,
  isOpen: bool,
  isMobile: bool,

  // are used on mobile to create the breadcrumb menu (in setActiveItems)
  isBreadCrumb: bool,
  renderBreadCrumb: func,

  icons: shape({
    iconActive: node,
    iconInactive: node
  }),
  // Will warn sometimes until all are changed to Styled-Components: string VS array //
  styles: shape({
    ulCommon: oneOfType([array, string]),
    ulActive: oneOfType([array, string]),
    ulInactive: oneOfType([array, string]),
    liCommon: oneOfType([array, string]),
    liActive: oneOfType([array, string]),
    liInactive: oneOfType([array, string]),
    aCommon: oneOfType([array, string]),
    aActive: oneOfType([array, string]),
    aInactive: oneOfType([array, string]),
    firstChildStyles: object,
    lastChildStyles: object
  })
};

function mapStateToProps(state) {
  const route = state.router.location.pathname;
  const orgRouteMatch = matchPath(route, { path: ORG_PATHS.BASE });
  const organizationId = orgRouteMatch?.params?.organizationId;
  const hasPurchaseOnBehalfOf = ENABLE_ROLES_PERMISSIONS ? getPurchaseAccess(state, organizationId) : true;
  const orgActiveBillingAccounts =
    getSelectedProfileBillingAccounts(state).filter(
      ba => ba.active && ba.userCanAccess
    ) || [];

  return {
    organizationId,
    hasPurchaseOnBehalfOf,
    billingAccounts: orgActiveBillingAccounts,
  };
}

function mapDispatchToProps(dispatch) {
  return {};
}

export default connect(mapStateToProps, mapDispatchToProps)(OrgNavMenu);